import React, { useState, useEffect, useMemo, Component } from 'react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-quartz.css';
import { AgGridReact } from 'ag-grid-react';
import '../../Assets/Style/style.css';
import LeaseApiService from '../../services/apiService';
import { endPoint } from '../../EndPoints/endPoint';
import { RiRefreshLine } from 'react-icons/ri';
import { FaFileCircleExclamation, FaFileExport } from 'react-icons/fa6';
import { FaInfoCircle } from 'react-icons/fa';
import { IoLockClosedOutline, IoLockOpenOutline } from 'react-icons/io5';
import Select from 'react-select';
import Modal from 'react-bootstrap/Modal';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { ClipLoader } from 'react-spinners';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import moment from 'moment';
import ColumnFilterHeader from './columnFilterHeader';
import Pagination from '../pagination/pagination';
import SummaryStrip, { countBy, countDistinct, totalFrom } from '../shared/summaryStrip';
import '../customerMaster/customerListing.css';
import { FiDownload, FiRefreshCw, FiRotateCcw, FiSearch } from 'react-icons/fi';
import RangeChips from '../shared/rangeChips';
import { useRange, fetchRanged, useCardFilter, valueTiles } from '../shared/rangedList';

/* This page is shared by both the "Unit Block" and "Unit Release" menu items
   (see releaseListing.js), controlled via the `mode` prop ('block' | 'release').
   The filters + grid are identical; only the bulk action button/modal differ. */

const selectTheme = (theme) => ({ ...theme, borderRadius: 5, padding: 9 });
const selectStyles = {
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (provided, state) => ({
    ...provided,
    backgroundColor: state.isFocused ? '#F5F5F5 ' : null,
    color: state.isFocused ? '#000 ' : '#000',
    ':hover': { backgroundColor: '#F5F5F5', color: '#000 ' },
  }),
};

const ALL_STATUS_OPTION = { value: { value: 'All', text: 'All' }, label: 'All' };

const getSelectValue = (option) => {
  if (!option) return '';
  return option?.value?.value ?? option?.label ?? '';
};

const emptyValueFormatter = (params) => {
  const v = params.value;
  if (v === null || v === undefined || v === '' || v === '0') return '--';
  return v;
};

const dateFormatter = (params) => {
  if (!params.value) return '--';
  const date = moment(params.value);
  return date.isValid() ? date.format('DD-MMM-YYYY') : '--';
};

// GetAllUnitBlockings' response shape is now confirmed against the live
// Swagger schema: { ref_No, unit_No, property_No, property_Name, status,
// blockRemarks, salesperson, block_Date, expiryDate }. It does NOT include
// unit type, rent per annum, area, or createdBy at all - those live on
// UnitManagement/GetAll instead, keyed by property_No + unit_No, and are
// resolved against `unitDetailsMap` at render time (see resolveUnitDetails),
// the same way `customerNo` is resolved to a display name via customerOptions.
const mapUnitRow = (item) => ({
  key: item?.ref_No || item?.refNo || item?.unit_No || `unit-${Math.random().toString(36).slice(2)}`,
  refNo: item?.ref_No || item?.refNo,
  propertyNo: item?.property_No || item?.propertyNo || '',
  propertyName: item?.property_Name || item?.propertyName || '',
  unitNo: item?.unit_No || item?.unitNo || '',
  status: item?.status || item?.unitStatus || '',
  expiryDate: item?.expiryDate || item?.expiry_Date || item?.blockExpiryDate || '',
  // Confirmed from a live raw row sample: the API calls this "salesperson"
  // (matching the write payload's field). It's a customer code, not a name -
  // resolved to a display name against the Customer list at render time.
  customerNo: item?.salesperson || item?.customerNo || item?.customer_No || '',
  createdBy: item?.createdBy || item?.created_By || '',
});

const blankActionForm = () => ({ status: null, customer: null, expiryDate: null, remarks: '' });

const UnitBlockingListing = ({ mode = 'block' }) => {
  const { t } = useTranslation();
  const isRelease = mode === 'release';
  const apiService = new LeaseApiService();
  const [loading, setLoading] = useState(true);
  const [rowData, setRowData] = useState([]);
  const [FilterTab, setFilterTab] = useState(true);
  const [propertyOptions, setPropertyOptions] = useState([]);
  const [unitOptions, setUnitOptions] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [unitDetailsMap, setUnitDetailsMap] = useState({});
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [gridApi, setGridApi] = useState(null);
  const [columnFilters, setColumnFilters] = useState({});
  const [showActionModal, setShowActionModal] = useState(false);
  const [actionForm, setActionForm] = useState(blankActionForm());
  const [saving, setSaving] = useState(false);
  const [pageSize, setPageSize] = useState(20);
  const [pagination, setPagination] = useState();
  // Drives both the modal's Expiry Date field visibility and its
  // required/sent status in handleSaveBlock - only "Blocked" and "Available"
  // use an expiry date.
  const EXPIRY_DATE_STATUSES = ['blocked', 'available'];
  const requiresExpiryDate = EXPIRY_DATE_STATUSES.includes((actionForm.status?.label || '').trim().toLowerCase());

  const [formData, setFormData] = useState({
    propertyNo: null,
    unitNo: null,
    status: ALL_STATUS_OPTION,
  });

  useEffect(() => {
    fetchDropdowns();
    fetchCustomers();
    fetchUnitDetails();
    getItems();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // The backend doesn't always group GetMultipleDropdown's response under the
  // exact key you requested in `dropdownTypes` (seen before on the Installment
  // Invoice page). Prefer the expected key, but fall back to whatever array is
  // actually in the response so the dropdown doesn't silently stay empty.
  const extractDropdownArray = (dropdowns, preferredKey) => {
    if (!dropdowns) return [];
    if (Array.isArray(dropdowns[preferredKey])) return dropdowns[preferredKey];
    const firstArray = Object.values(dropdowns).find((v) => Array.isArray(v) && v.length);
    return firstArray || [];
  };

  const toOption = (item) => ({ value: item, label: item?.text ?? item?.label ?? item?.name ?? item?.value ?? '' });

  const fetchDropdowns = async () => {
    try {
      const propParams = new URLSearchParams();
      propParams.append('dropdownTypes', 'UBSearchProperties');
      const propRes = await apiService.getApiParamater('UNITBLOCKING_MULTIDROP', `?${propParams.toString()}`);
      const properties = extractDropdownArray(propRes?.data?.dropdowns, 'UBSearchProperties');
      setPropertyOptions(properties.map(toOption));
    } catch (error) {
      console.error('Error loading property dropdown:', error);
    }

    try {
      const statusParams = new URLSearchParams();
      statusParams.append('dropdownTypes', 'UBSearchStatus');
      const statusRes = await apiService.getApiParamater('UNITBLOCKING_MULTIDROP', `?${statusParams.toString()}`);
      const statuses = extractDropdownArray(statusRes?.data?.dropdowns, 'UBSearchStatus');
      setStatusOptions(statuses.map(toOption));
    } catch (error) {
      console.error('Error loading status dropdown:', error);
    }
  };

  const fetchUnitOptions = async (propertyValue) => {
    if (!propertyValue) {
      setUnitOptions([]);
      return;
    }
    setLoadingUnits(true);
    try {
      const params = new URLSearchParams();
      params.append('DropdownFor', 'CustUnitNoByPropertyNo');
      params.append('CascadValue', propertyValue);
      const res = await apiService.getApiParamater('CUSTOMER_CASCAD', `?${params.toString()}`);
      setUnitOptions(res?.data?.items || []);
    } catch (error) {
      console.error('Error loading unit dropdown:', error);
      setUnitOptions([]);
    } finally {
      setLoadingUnits(false);
    }
  };

  const fetchCustomers = async () => {
    if (customerOptions.length) return;
    setLoadingCustomers(true);
    try {
      const params = new URLSearchParams();
      params.append('PageNo', '1');
      params.append('PageSize', '500');
      params.append('FullName', '');
      const res = await apiService.getApiParamater('GET_CUSTOMER', `?${params.toString()}`);
      const rows = res?.data?.result || [];
      setCustomerOptions(rows.map((c) => ({ value: c?.customerNo, label: c?.customerName || c?.customerNo })));
    } catch (error) {
      console.error('Error loading customers:', error);
      setCustomerOptions([]);
    } finally {
      setLoadingCustomers(false);
    }
  };

  const resolveCustomerName = (customerNo) => {
    if (!customerNo) return '';
    const match = customerOptions.find((c) => String(c.value) === String(customerNo));
    return match?.label || customerNo;
  };

  // UnitBlocking's own endpoints don't carry unit type / rent / area (confirmed
  // against the live Swagger schema) - those live on UnitManagement/GetAll,
  // keyed by property_No + unit_No. Fetch once and look them up at render time,
  // the same way customerNo is resolved to a display name above.
  const fetchUnitDetails = async () => {
    try {
      const params = new URLSearchParams();
      params.append('PageNo', '1');
      params.append('PageSize', '2000');
      const res = await apiService.getApiParamater('GET_UNIT_LIST', `?${params.toString()}`);
      const units = res?.data?.result || [];
      const map = {};
      units.forEach((u) => {
        const key = `${u?.property_No || ''}||${u?.unit_No || ''}`;
        map[key] = {
          unitType: u?.unit_Type || '',
          totalArea: u?.total_Area,
          rentPerAnnum: u?.rent_Per_Annum,
        };
      });
      setUnitDetailsMap(map);
    } catch (error) {
      console.error('Error loading unit details for enrichment:', error);
    }
  };

  const resolveUnitDetails = (row) => unitDetailsMap[`${row?.propertyNo || ''}||${row?.unitNo || ''}`] || {};

  const handlePropertyChange = (selectedOption) => {
    setFormData((prev) => ({ ...prev, propertyNo: selectedOption, unitNo: null }));
    fetchUnitOptions(selectedOption?.value?.value);
  };

  // Only requests the single page asked for (default: page 1) - previously this
  // pulled up to 200 rows in one shot regardless of what the user was viewing.
  // Page 2+ is only fetched when the user actually clicks that page.
  const { range, setRange, cache: rangeCache } = useRange(() => getItems(1));
  const cf = useCardFilter(rowData, range, rangeCache);
  const getItems = async (newPageNo = pagination?.currentPage || 1) => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('PageNo', newPageNo || 1);
      params.append('PageSize', pageSize || 20);
      params.append('PropertyNo', getSelectValue(formData.propertyNo));
      params.append('UnitNo', formData.unitNo?.value || '');
      params.append('Status', getSelectValue(formData.status) !== 'All' ? getSelectValue(formData.status) : '');
      const res = await fetchRanged({ apiService, endpoint: isRelease ? 'GET_ALL_UNIT_BLOCKINGS_RELEASE' : 'GET_ALL_UNIT_BLOCKINGS', params, range, cache: rangeCache, pageNo: newPageNo, pageSize });
      const rawRows = res?.data?.result || [];
      const rows = rawRows.map(mapUnitRow);
      const missingRefNo = rows.filter((r) => !r.refNo).length;
      if (missingRefNo) {
        console.warn(`[UnitBlocking] ${missingRefNo} row(s) have no ref_No in the API response - those units may need backend data fixed.`);
      }
      setRowData(rows);
      setPagination(res?.data?.paginationData);
      setSelectedRows([]);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching units:', error);
      setRowData([]);
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setFormData({ propertyNo: null, unitNo: null, status: ALL_STATUS_OPTION });
    setUnitOptions([]);
    setColumnFilters({});
    setTimeout(() => getItems(1), 0);
  };

  const onSelectionChanged = () => {
    if (gridApi) {
      setSelectedRows(gridApi.getSelectedRows());
    }
  };

  const openActionModal = () => {
    if (selectedRows.length === 0) {
      toast.warning(`Select at least one unit to ${isRelease ? 'release' : 'block'}.`);
      return;
    }
    setActionForm(blankActionForm());
    if (!isRelease) fetchCustomers();
    setShowActionModal(true);
  };

  const closeActionModal = () => {
    if (saving) return;
    setShowActionModal(false);
  };

  const handleSaveBlock = async () => {
    if (!actionForm.status) {
      toast.warning('Please select a Unit Status');
      return;
    }
    if (!actionForm.customer) {
      toast.warning('Please select a Customer');
      return;
    }
    // Expiry Date only makes sense (and is only required/sent) when the chosen
    // status is "Blocked" - other statuses don't expire the same way.
    if (requiresExpiryDate && !actionForm.expiryDate) {
      toast.warning('Please select an Expiry Date');
      return;
    }
    if (!actionForm.remarks.trim()) {
      toast.warning('Please enter Remarks');
      return;
    }
    // refNo must come from the row's actual ref_No - never fall back to
    // unitNo, since sending the wrong identifier for any row would make that
    // row silently fail (or apply to a different row) server-side.
    const refNos = [...new Set(selectedRows.map((row) => row.refNo).filter(Boolean))];
    console.log('[UnitBlocking] Block - selectedRows:', selectedRows);
    if (refNos.length !== selectedRows.length) {
      console.warn('[UnitBlocking] Some selected rows have no ref_No (or are duplicates) and were dropped from the block request.');
    }
    if (refNos.length === 0) {
      toast.error('Selected unit(s) have no ref_No from the API - cannot block. Check console for the raw row data.');
      return;
    }
    try {
      setSaving(true);
      // Schema confirmed via a working Swagger "Try it out" call: camelCase
      // fields, full ISO datetimes. blockDate is NOT required (confirmed by a
      // successful Swagger call that omitted it) - left out on purpose.
      // "salesperson" is the API's own name for what this UI calls "Customer".
      // NOTE: one shared status/customer/expiryDate/remarks is sent alongside
      // the whole refNos array - if only the first unit ends up updated after
      // a multi-unit save, the bulk endpoint on the backend is only applying
      // these fields to refNos[0] instead of looping over the full array.
      const body = {
        refNos,
        blockRemarks: actionForm.remarks,
        status: getSelectValue(actionForm.status),
        salesperson: actionForm.customer?.value,
        ...(requiresExpiryDate && actionForm.expiryDate ? { expiryDate: new Date(actionForm.expiryDate).toISOString() } : {}),
      };
      console.log('[UnitBlocking] POST BulkUnitBlocking body:', JSON.stringify(body, null, 2));
      const startedAt = Date.now();
      const res = await apiService.post('BULK_UNIT_BLOCKING', body);
      console.log(`[UnitBlocking] BulkUnitBlocking responded in ${Date.now() - startedAt}ms`, res?.data);
      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201 || res?.data?.statusCode === 207) {
        // The wrapper's top-level statusCode only reflects that the HTTP call
        // succeeded - it does NOT mean every refNo was actually blocked. The
        // real per-item outcome is in result.{successCount,failedCount,failedRefNos}
        // (BulkUnitBlockingResponseDto), which a 200/201 can still carry with
        // failedCount > 0. Trusting statusCode alone reports false positives.
        const result = res?.data?.result;
        const failedCount = result?.failedCount ?? 0;
        const successCount = result?.successCount ?? refNos.length;
        if (failedCount > 0) {
          toast.warning(
            result?.message ||
              `${successCount} of ${result?.totalCount ?? refNos.length} unit(s) blocked. ${failedCount} failed${
                result?.failedRefNos?.length ? ` (ref ${result.failedRefNos.join(', ')})` : ''
              }.`
          );
        } else {
          toast.success(result?.message || res?.data?.message || 'Units blocked successfully');
        }
        setShowActionModal(false);
        getItems();
      } else {
        toast.warning(res?.data?.message || 'Failed to block units');
      }
    } catch (error) {
      console.error('[UnitBlocking] BulkUnitBlocking failed:', error?.response?.status, error?.response?.data || error?.message);
      toast.error(error?.response?.data?.message || 'Failed to block units.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveRelease = async () => {
    // Despite the field being named "refNos", the backend's release lookup is
    // keyed off the unit_No, not ref_No - confirmed by comparing a working
    // Swagger call against the app's request (ref_No consistently returned
    // failedCount:1 with no data change; unit_No released successfully).
    const refNos = [...new Set(selectedRows.map((row) => row.unitNo).filter(Boolean))];
    console.log('[UnitBlocking] Release - selectedRows:', selectedRows);
    if (refNos.length !== selectedRows.length) {
      console.warn('[UnitBlocking] Some selected rows have no unit_No (or are duplicates) and were dropped from the release request.');
    }
    if (refNos.length === 0) {
      toast.error('Selected unit(s) have no unit_No from the API - cannot release. Check console for the raw row data.');
      return;
    }
    try {
      setSaving(true);
      // Confirmed working schema: { refNos: [...], releasedDate }. No remarks
      // field - sending one (an unrecognized key) was very likely what caused
      // the earlier "Invalid column name" errors.
      // NOTE: one shared releasedDate is sent alongside the whole refNos array
      // - if only the first unit ends up released after a multi-unit save,
      // the bulk endpoint on the backend is only applying it to refNos[0]
      // instead of looping over the full array.
      const body = { refNos, releasedDate: new Date().toISOString() };
      // Full outgoing request - method/url/headers/body - so this can be
      // replayed 1:1 in Swagger's "Try it out" to compare against the app.
      // Headers are read straight from localStorage/axios defaults, i.e.
      // exactly what the axios request interceptor attaches to this call.
      console.log('[UnitBlocking] PUT BulkReleaseUnitBlocking request:', {
        url: endPoint.BULK_RELEASE_UNIT_BLOCKING,
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          apiKey: 'V+kBloEwqqVho1PINmQI0VJQwSBX2l/e',
          Authorization: `Bearer ${localStorage.getItem('Token')}`,
          DB: localStorage.getItem('DataBase'),
        },
        body,
      });
      const startedAt = Date.now();
      const res = await apiService.put('BULK_RELEASE_UNIT_BLOCKING', body);
      console.log(`[UnitBlocking] BulkReleaseUnitBlocking responded in ${Date.now() - startedAt}ms`, res?.data);
      if (res?.data?.statusCode === 200 || res?.data?.statusCode === 201 || res?.data?.statusCode === 207) {
        // Per Swagger, the wrapper's top-level statusCode only reflects that the
        // HTTP call itself succeeded - the real per-item outcome lives in
        // result.{successCount,failedCount,failedRefNos} (BulkReleaseResponseDto).
        // A 200 can still carry failedCount > 0 (e.g. this ref_No wasn't actually
        // released), which is exactly what was showing as a false "success" toast
        // while the unit stayed blocked in the grid.
        const result = res?.data?.result;
        const failedCount = result?.failedCount ?? 0;
        const successCount = result?.successCount ?? refNos.length;
        if (failedCount > 0) {
          toast.warning(
            result?.message ||
              `${successCount} of ${result?.totalCount ?? refNos.length} unit(s) released. ${failedCount} failed${
                result?.failedRefNos?.length ? ` (ref ${result.failedRefNos.join(', ')})` : ''
              }.`
          );
        } else {
          toast.success(result?.message || res?.data?.message || 'Units released successfully');
        }
        setShowActionModal(false);
        getItems();
      } else {
        toast.warning(res?.data?.message || 'Failed to release units');
      }
    } catch (error) {
      console.error('[UnitBlocking] BulkReleaseUnitBlocking failed:', error?.response?.status, error?.response?.data || error?.message);
      toast.error(error?.response?.data?.message || 'Failed to release units.');
    } finally {
      setSaving(false);
    }
  };

  const exportPDF = () => {
    if (!filteredRowData?.length) {
      alert('No data available to export');
      return;
    }
    const doc = new jsPDF('landscape');
    const primaryColor = [110, 81, 153];
    doc.setFillColor(...primaryColor);
    doc.rect(0, 0, 297, 25, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.text(isRelease ? 'UNIT RELEASE LIST REPORT' : 'UNIT BLOCK LIST REPORT', 148.5, 15, { align: 'center' });
    doc.setFontSize(9);
    doc.text(`Generated On: ${new Date().toLocaleDateString()}`, 280, 21, { align: 'right' });
    doc.setTextColor(0, 0, 0);

    const tableColumn = ['Property', 'Unit', 'Status', 'Unit Type', 'Rent Per Annum', 'Area (Sq. Ft.)', 'Expiry Date', 'Customer Name', 'Created By'];
    const tableRows = filteredRowData.map((item) => {
      const details = resolveUnitDetails(item);
      return [
        item.propertyName || '-',
        item.unitNo || '-',
        item.status || '-',
        details.unitType || '-',
        details.rentPerAnnum ?? '-',
        details.totalArea ?? '-',
        item.expiryDate ? moment(item.expiryDate).format('DD-MMM-YYYY') : '-',
        resolveCustomerName(item.customerNo) || '-',
        item.createdBy || '-',
      ];
    });

    doc.autoTable({
      startY: 35,
      head: [tableColumn],
      body: tableRows,
      theme: 'grid',
      styles: { fontSize: 8, cellPadding: 3, valign: 'middle', lineColor: [220, 220, 220], lineWidth: 0.1 },
      headStyles: { fillColor: primaryColor, textColor: [255, 255, 255], fontStyle: 'bold', halign: 'center', fontSize: 9 },
      alternateRowStyles: { fillColor: [248, 246, 252] },
      bodyStyles: { textColor: [50, 50, 50] },
      margin: { left: 10, right: 10 },
    });
    doc.save(isRelease ? 'Unit_Release_List.pdf' : 'Unit_Block_List.pdf');
  };

  const displayValue = (row, field, formatter, accessor) => {
    const raw = accessor ? accessor(row) : row?.[field];
    return formatter ? formatter({ value: raw }) : emptyValueFormatter({ value: raw });
  };
  const buildFilterParams = (field, formatter, accessor) => ({
    getValues: () => rowData.map((r) => displayValue(r, field, formatter, accessor)),
    selected: columnFilters[field],
    onChange: (vals) => setColumnFilters((prev) => ({ ...prev, [field]: vals })),
    onClear: () =>
      setColumnFilters((prev) => {
        const next = { ...prev };
        delete next[field];
        return next;
      }),
  });

  const unitDetailAccessors = {
    unitType: (r) => resolveUnitDetails(r).unitType,
    rentPerAnnum: (r) => resolveUnitDetails(r).rentPerAnnum,
    totalArea: (r) => resolveUnitDetails(r).totalArea,
    customerName: (r) => resolveCustomerName(r.customerNo),
  };

  const filteredRowData = useMemo(() => {
    const activeFields = Object.keys(columnFilters).filter((f) => columnFilters[f] && columnFilters[f].length);
    if (!activeFields.length) return rowData;
    return rowData.filter((row) =>
      activeFields.every((field) => {
        const formatter = field === 'expiryDate' ? dateFormatter : undefined;
        const accessor = unitDetailAccessors[field];
        return columnFilters[field].includes(displayValue(row, field, formatter, accessor));
      }),
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowData, columnFilters, customerOptions, unitDetailsMap]);

  const columnDefs = [
    {
      headerName: '',
      field: 'select',
      headerCheckboxSelection: true,
      checkboxSelection: true,
      suppressMovable: true,
      sortable: false,
      filter: false,
      width: 44,
      pinned: 'left',
    },
    {
      headerName: t('unitBlock.columns.property'),
      field: 'propertyName',
      suppressMovable: true,
      sortable: true,
      width: 180,
      pinned: 'left',
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('propertyName'),
    },
    {
      headerName: t('unitBlock.columns.unit'),
      field: 'unitNo',
      suppressMovable: true,
      sortable: true,
      width: 110,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('unitNo'),
    },
    {
      headerName: t('unitBlock.columns.status'),
      field: 'status',
      suppressMovable: true,
      sortable: true,
      width: 130,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('status'),
    },
    {
      headerName: t('unitBlock.columns.unitType'),
      suppressMovable: true,
      sortable: true,
      width: 130,
      valueGetter: (params) => resolveUnitDetails(params.data).unitType || '--',
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('unitType', undefined, unitDetailAccessors.unitType),
    },
    {
      headerName: t('unitBlock.columns.rentPerAnnum'),
      suppressMovable: true,
      sortable: true,
      width: 150,
      valueGetter: (params) => resolveUnitDetails(params.data).rentPerAnnum ?? '--',
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('rentPerAnnum', undefined, unitDetailAccessors.rentPerAnnum),
    },
    {
      headerName: t('unitBlock.columns.area'),
      suppressMovable: true,
      sortable: true,
      width: 140,
      valueGetter: (params) => resolveUnitDetails(params.data).totalArea ?? '--',
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('totalArea', undefined, unitDetailAccessors.totalArea),
    },
    {
      headerName: t('unitBlock.columns.expiryDate'),
      field: 'expiryDate',
      suppressMovable: true,
      sortable: true,
      width: 130,
      valueFormatter: dateFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('expiryDate', dateFormatter),
    },
    {
      headerName: t('unitBlock.columns.customerName'),
      suppressMovable: true,
      sortable: true,
      width: 200,
      valueGetter: (params) => resolveCustomerName(params.data?.customerNo) || '--',
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('customerName', undefined, (row) => resolveCustomerName(row.customerNo)),
    },
    {
      headerName: t('unitBlock.columns.createdBy'),
      field: 'createdBy',
      suppressMovable: true,
      sortable: true,
      width: 150,
      valueFormatter: emptyValueFormatter,
      headerComponent: ColumnFilterHeader,
      headerComponentParams: buildFilterParams('createdBy'),
    },
  ];

  return (
    <div className="unitblock-list-page cl-page">
      <div className="cl-titlebar">
        <div className="cl-title">
        <h1>{isRelease ? 'Unit Release' : 'Unit Block'}</h1>
        <p>{isRelease ? 'Release blocked units so they can be leased or sold again.' : 'Hold units for a customer until a set expiry date.'}</p>
      </div>
        <RangeChips value={range} onChange={setRange} />
      </div>
      <SummaryStrip
        label={isRelease ? 'Total blocked units' : 'Total units'}
        total={totalFrom(pagination, filteredRowData.length)}
        subtitle={`${selectedRows.length} selected`}
        heroNote={cf.inPeriod ? 'Chosen period' : 'All records'} heroActive={!cf.filter} onHero={() => cf.setFilter(null)}
        tiles={[
          { label: cf.inPeriod ? 'In period' : 'On this page', value: cf.statsRows.length, note: 'units', active: !cf.filter, onClick: () => cf.setFilter(null) },
          ...valueTiles(cf, 'status', 3),
          { label: 'Properties', value: countDistinct(cf.statsRows, 'propertyName') },
        ]}
        ariaLabel="Unit summary"
      />
      <section className="cl-panel cl-filters" aria-label={t('common.searchParameters')}>
        <div>
          <div>
            <div className="d-flex gap-3 align-items-stretch">
              <div className="form-group grid-contain flex-grow-1">
                <div className="d-flex">
                  <label className="form-label pt-2 start">{t('unitBlock.filters.property')}</label>
                  <Select
                    className="p-0 w-100"
                    theme={selectTheme}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={formData.propertyNo}
                    onChange={handlePropertyChange}
                    options={propertyOptions}
                    isClearable
                    placeholder={t('unitBlock.placeholders.selectProperty')}
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-2 start">{t('unitBlock.filters.unit')}</label>
                  <Select
                    className="p-0 w-100"
                    theme={selectTheme}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={formData.unitNo}
                    isDisabled={loadingUnits || !formData.propertyNo}
                    onChange={(selectedOption) => setFormData((prev) => ({ ...prev, unitNo: selectedOption }))}
                    options={unitOptions?.map((item) => ({ value: item?.value, label: item?.text }))}
                    isClearable
                    placeholder={t('unitBlock.placeholders.selectUnit')}
                  />
                </div>
                <div className="d-flex">
                  <label className="form-label pt-2 start">{t('unitBlock.filters.unitStatus')}</label>
                  <Select
                    className="p-0 w-100"
                    theme={selectTheme}
                    menuPortalTarget={document.body}
                    styles={selectStyles}
                    value={formData.status}
                    onChange={(selectedOption) => setFormData((prev) => ({ ...prev, status: selectedOption }))}
                    options={[ALL_STATUS_OPTION, ...statusOptions]}
                    placeholder={t('unitBlock.placeholders.selectStatus')}
                  />
                </div>
              </div>
              <div className="cl-filter-actions-inline">
                <button type="button" className="cl-btn cl-btn-ghost" onClick={() => handleClearFilters()}><FiRotateCcw size={16} />{t('common.reset')}</button>
                <button type="button" className="cl-btn cl-btn-primary" onClick={() => getItems(1)}><FiSearch size={16} />{t('common.search')}</button>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="cl-panel cl-list">
        {loading ? (
          <div>{t('common.loading')}</div>
        ) : (
          <>
            <div className="cl-list-head">
              <button className="cl-btn cl-btn-primary" type="button" onClick={openActionModal} disabled={selectedRows.length === 0}>
                {isRelease ? (
                  <IoLockOpenOutline color="#fff" className="center mt-1" />
                ) : (
                  <IoLockClosedOutline color="#fff" className="center mt-1" />
                )}{' '}
                <span>{isRelease ? t('unitBlock.releaseUnits') : t('unitBlock.blockUnits')}</span>
              </button>
              <div className="d-flex align-items-center gap-3">
                <span className="unitblock-total-count">
                  {t('unitBlock.totalUnits', {
                    status: getSelectValue(formData.status) !== 'All' ? `${getSelectValue(formData.status)} ` : '',
                    // Column filters only operate on the current page's rows, so
                    // their count is shown while active; otherwise this reflects
                    // every page, not just the one currently loaded.
                    count: Object.keys(columnFilters).length ? filteredRowData.length : pagination?.totalRecords ?? filteredRowData.length,
                  })}
                </span>
                <button type="button" className="cl-btn cl-btn-ghost cl-btn-sm" onClick={() => getItems()}><FiRefreshCw size={16} />{t('common.refresh')}</button>
                <button type="button" className="cl-btn cl-btn-ghost cl-btn-sm" onClick={() => exportPDF()}><FiDownload size={16} />{t('common.export')}</button>
              </div>
            </div>
            <div className="cl-grid-wrap">
              <div className="ag-theme-quartz unitblock-grid customer-grid" style={{ height: 650 }}>
                <AgGridReact
                  onGridReady={(params) => setGridApi(params.api)}
                                    rowData={cf.filter ? cf.shownRows : filteredRowData}
                  columnDefs={columnDefs}
                  rowSelection="multiple"
                  suppressRowClickSelection={true}
                  onSelectionChanged={onSelectionChanged}
                  getRowId={(params) => params.data.key}
                />
              </div>
              {!cf.filter && <Pagination pageSize={pageSize} setPageSize={setPageSize} pagination={pagination} getItems={getItems} />}
            </div>
          </>
        )}
      </section>

      <Modal show={showActionModal} onHide={closeActionModal} centered backdrop="static" keyboard={false} dialogClassName="unitblock-modal">
        <Modal.Header closeButton>
          <Modal.Title>{isRelease ? t('unitBlock.releaseUnits') : t('unitBlock.blockUnits')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="unitblock-info-banner">
            <FaInfoCircle size={16} />
            <span>
              {isRelease
                ? t('unitBlock.releasingInfo', { count: selectedRows.length })
                : t('unitBlock.blockingInfo', { count: selectedRows.length })}
            </span>
          </div>
          {!isRelease && (
            <>
              <div className="mb-3">
                <label className="form-label">
                  {t('unitBlock.modal.unitStatus')}<span className="text-danger">*</span>
                </label>
                <Select
                  theme={selectTheme}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  value={actionForm.status}
                  onChange={(selectedOption) => setActionForm((prev) => ({ ...prev, status: selectedOption }))}
                  options={statusOptions}
                  placeholder={t('unitBlock.placeholders.selectStatus')}
                />
              </div>
              <div className="mb-3">
                <label className="form-label">
                  {t('unitBlock.modal.customer')}<span className="text-danger">*</span>
                </label>
                <Select
                  theme={selectTheme}
                  menuPortalTarget={document.body}
                  styles={selectStyles}
                  value={actionForm.customer}
                  isLoading={loadingCustomers}
                  onChange={(selectedOption) => setActionForm((prev) => ({ ...prev, customer: selectedOption }))}
                  options={customerOptions}
                  placeholder={t('unitBlock.placeholders.selectCustomer')}
                />
              </div>
              {requiresExpiryDate && (
                <div className="mb-3">
                  <label className="form-label">
                    {t('unitBlock.modal.expiryDate')}<span className="text-danger">*</span>
                  </label>
                  <DatePicker
                    selected={actionForm.expiryDate}
                    onChange={(date) => setActionForm((prev) => ({ ...prev, expiryDate: date }))}
                    dateFormat="dd-MMM-yyyy"
                    className="p-2 form-control w-100 datepicker-icon"
                    placeholderText={t('unitBlock.placeholders.selectExpiryDate')}
                  />
                </div>
              )}
            </>
          )}
          {!isRelease && (
            <div>
              <label className="form-label d-flex justify-content-between">
                <span>
                  {t('unitBlock.modal.remarks')}<span className="text-danger">*</span>
                </span>
                <span className="text-muted small">{actionForm.remarks.length}/250</span>
              </label>
              <textarea
                className="form-control"
                rows={4}
                maxLength={250}
                value={actionForm.remarks}
                onChange={(e) => setActionForm((prev) => ({ ...prev, remarks: e.target.value }))}
                placeholder={t('unitBlock.placeholders.enterRemarks')}
              />
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button className="mainbtnRed" onClick={closeActionModal} disabled={saving}>
            {t('common.cancel')}
          </button>
          <button className="mainbtn" onClick={isRelease ? handleSaveRelease : handleSaveBlock} disabled={saving}>
            {saving ? <ClipLoader color="#ffffff" size={18} /> : t('common.save')}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default UnitBlockingListing;
